import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenSiteTabCustomization } from '@gitbook/app-navigation';

import type { UseUpgradeSite } from '../../state';

/**
 * Props for a panel in the Site customization step.
 */
export type SitesCustomizationPanelProps<
    Settings extends api.SiteCustomizationSettings | api.SiteSpaceCustomizationSettings =
        | api.SiteCustomizationSettings
        | api.SiteSpaceCustomizationSettings,
> = {
    // The current settings of a Site or SiteSpace
    settings: Settings;

    // Configure new settings
    setSettings: React.Dispatch<
        React.SetStateAction<
            Settings extends api.SiteSpaceCustomizationSettings
                ? SiteSpaceCustomizationOverrides
                : SiteCustomizationUpdate
        >
    >;

    /** Organization ID */
    organizationId: string;

    /** Site */
    site: api.Site;

    siteSpaceId?: SiteCustomizationBodyProps['siteSpaceId'];

    /** Upgrade actions */
    upgrade: UseUpgradeSite | null;

    /** All space IDs for the current site */
    siteSpaceIds: string[];
};

export type SiteOnlyCustomizationProps =
    SitesCustomizationPanelProps<api.SiteCustomizationSettings>;

export type SiteCustomizationBodyProps = {
    siteSpaces?: api.SiteSpace[];

    /** The currently selected tab */
    selectedTab: NavigationScreenSiteTabCustomization['tab'];

    /** Set the currently selected tab */
    setSelectedTab: (
        tab: NavigationScreenSiteTabCustomization['tab'],
        siteSpaceId?: api.SiteSpace['id']
    ) => void;

    /** Currently shown siteSpace in the customization modal */
    siteSpaceId?: api.SiteSpace['id'];
};

export type SiteSpaceCustomizationOverrides =
    api.OperationSpec<'overrideSiteSpaceCustomizationById'>['RequestBody'];

export type SiteCustomizationUpdate =
    api.OperationSpec<'updateSiteCustomizationById'>['RequestBody'];

/**
 * Return true if we are editing the customization of a site, not a site space.
 */
export function isSiteOnlyCustomization(
    props: SitesCustomizationPanelProps
): props is SitesCustomizationPanelProps<api.SiteCustomizationSettings> {
    return !props.siteSpaceId;
}
